import type { MindChatAction } from '../services/MendsVisionService';
import { FOLLOW_UP_NAVIGATE_PAYLOAD } from './aiActions';

/**
 * Defesa em profundidade: remove vazamento de JSON/cercas ```json da reply
 * e tenta recuperar actions se o backend não as parseou.
 */
export function sanitizeMindReply(
  reply: string,
  existing: MindChatAction[] = [],
): { reply: string; actions: MindChatAction[] } {
  let text = (reply || '').replace(/\r\n/g, '\n');
  const recovered: MindChatAction[] = [...existing];

  const ingest = (raw: string) => {
    for (const a of extractActionsFromJsonish(raw)) {
      if (!recovered.some((x) => x.path === a.path && x.title === a.title)) {
        recovered.push(a);
      }
    }
  };

  // ACTIONS_JSON=...
  const marker = text.match(/ACTIONS_JSON\s*=\s*(\{[\s\S]*)$/i);
  if (marker && marker.index != null) {
    ingest(marker[1]);
    text = text.slice(0, marker.index);
  }

  // Cercas ```json ... ``` (fechadas)
  text = text.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (_, body: string) => {
    ingest(body);
    return '';
  });

  // Cerca aberta até o fim
  text = text.replace(/```(?:json)?\s*([\s\S]*)$/gi, (_, body: string) => {
    ingest(body);
    return '';
  });

  // Trailer {"actions": ...} (completo ou truncado)
  const actionsKey = text.lastIndexOf('"actions"');
  if (actionsKey >= 0) {
    const brace = text.lastIndexOf('{', actionsKey);
    if (brace >= 0) {
      ingest(text.slice(brace));
      text = text.slice(0, brace);
    }
  }

  text = text
    .replace(/```\w*/g, '')
    .replace(/\{\s*"actions"\s*:\s*\[[\s\S]*$/gi, '')
    .replace(/^\s*json\s*/i, '')
    .trim();

  return {
    reply: text || 'Aqui está o que posso fazer a partir do seu pedido.',
    actions: recovered,
  };
}

function extractActionsFromJsonish(raw: string): MindChatAction[] {
  const out: MindChatAction[] = [];
  const trimmed = (raw || '').trim();
  if (!trimmed) return out;

  const tryParse = (s: string) => {
    try {
      const doc = JSON.parse(s) as { actions?: unknown[] };
      if (!Array.isArray(doc.actions)) return false;
      for (const el of doc.actions) {
        const a = normalizeAction(el);
        if (a) out.push(a);
      }
      return true;
    } catch {
      return false;
    }
  };

  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start >= 0 && end > start && tryParse(trimmed.slice(start, end + 1))) return out;
  if (start >= 0 && tryParse(`${trimmed.slice(start)}]}`)) return out;
  if (tryParse(trimmed)) return out;

  const re = /\{\s*"kind"\s*:\s*"[^"]+"[\s\S]*?\}/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(trimmed)) !== null) {
    try {
      const a = normalizeAction(JSON.parse(m[0]));
      if (a) out.push(a);
    } catch {
      /* ignore */
    }
  }
  return out;
}

function normalizeAction(el: unknown): MindChatAction | null {
  if (!el || typeof el !== 'object') return null;
  const o = el as Record<string, unknown>;
  const kind = String(o.kind || 'nav');
  const title = String(o.title || '').trim();
  const path = String(o.path || '').trim();
  if (!title || !path) return null;
  if (!isAllowedPath(path)) return null;
  const requiresConfirm =
    o.requiresConfirm === true
    || kind === 'mutation'
    || path.startsWith('mutation:');
  return {
    kind: requiresConfirm ? 'mutation' : kind,
    title,
    path,
    requiresConfirm,
    approvalTitle: requiresConfirm ? title : null,
    approvalBody: requiresConfirm ? `Proposta da Mends AI: ${title}` : null,
    suggestedAction: requiresConfirm ? 'Revisar e confirmar' : null,
    actionKind: requiresConfirm
      ? (path.startsWith('mutation:briefing') ? 'generate_briefing'
        : path.startsWith('mutation:follow-up-sweep') ? 'follow_up_sweep'
        // Só abre as regras de follow-up: navegação, não pergunta de texto (executor legado só navegava).
        : path.startsWith('mutation:follow-up') ? 'navigate'
        : path.startsWith('mutation:refresh-memory') ? 'refresh_memory'
        : path.startsWith('mutation:send-message') ? 'send_message'
        : path.startsWith('mutation:transfer-conversation') ? 'transfer_conversation'
        : null)
      : null,
    actionPayload: requiresConfirm && path.startsWith('mutation:follow-up') && !path.startsWith('mutation:follow-up-sweep')
      ? FOLLOW_UP_NAVIGATE_PAYLOAD
      : null,
  };
}

function isAllowedPath(path: string): boolean {
  const allowedPrefixes = [
    '/hoje', '/chat', '/aprovacoes', '/oportunidades', '/central-inteligencia',
    '/follow-up', '/contacts', '/mente', '/ai-agent', '/crm',
  ];
  if (allowedPrefixes.some((p) => path.toLowerCase().startsWith(p))) return true;
  return [
    'mutation:follow-up',
    'mutation:briefing',
    'mutation:refresh-memory',
    'mutation:send-message',
    'mutation:transfer-conversation',
    'mutation:contact-intel',
    'mutation:follow-up-sweep',
    'action:register-conversation',
    'action:register-voice',
  ].includes(path);
}
